import React, { useState, useEffect } from "react";
// import PropTypes from 'prop-types';
import {
  Layer,
  Box,
  Heading,
  Button,
  FormField,
  TextInput,
  Text,
  CheckBox,
} from "grommet";
import GuestService from "../../services/guest.service";
import { presentToastSuccess, presentToastErrorContent } from "@shared/Toast";
import { BookingCalendarInput, PhoneInput } from "@shared";
import moment from "moment-timezone";
import { RoomSelect } from "./";
import {
  parsePhoneNumber,
  formatPhoneNumberIntl,
  isValidPhoneNumber,
} from "react-phone-number-input";
import { presentLoading, dismissLoading } from "@shared/Loading";
import { useFetchHotel, useFetchDoors } from "./redux/hooks";
import "./GuestModal.scss";
import { useTranslation } from "react-i18next";
import { getOfferCapabilities } from "../../common/offerCapabilities";

const STEP_GUEST_PHONE = 0;
const STEP_GUEST_INFO = 1;
const STEP_CHECK_IN = 2;
const STEP_COMPLETE = 3;

const defaultValues = {
  _id: 0,
  title: "Add",
  firstname: "",
  lastname: "",
  email: "",
  areaCode: "",
  phone: "",
  areacode: "",
  checkinDate: moment().hour(15),
  checkoutDate: moment().add(1, "days").hour(12),
  roomNumber: "",
  roomType: 0,
  accessDoors: [],
  currentDoors: [],
  invitationSent: false,
};

export default function GuestModal(props) {
  const { t } = useTranslation();
  const { guestId, onClose, sendKeys } = props;
  const [step, setStep] = useState(0);
  const { hotel } = useFetchHotel();
  const { rooms } = useFetchDoors();
  const [formValues, setFormValues] = useState(defaultValues);

  const currentRoomType = rooms?.find(
    (r) => r.name === formValues.roomNumber,
  )?.roomType;
  const hasAvailableUpsells =
    getOfferCapabilities(hotel).upsells &&
    ((hotel.offerEarlyCheckin && currentRoomType?.earlyCheckinPrice > 0) ||
      (hotel.offerUpgrade && currentRoomType?.upgradePrice > 0) ||
      (hotel.offerLateCheckout && currentRoomType?.lateCheckoutPrice > 0));
  const [phoneNumberError, setPhoneNumberError] = useState("");

  useEffect(() => {
    if (guestId !== 0) {
      presentLoading();
      GuestService.getBooking(guestId).then(
        (response) => {
          dismissLoading();
          const booking = response.data;
          if (booking._id) {
            setFormValues({
              ...booking,
              title: "Edit",
              currentDoors: booking.accessDoors,
              checkinDate: moment.tz(booking.checkinDate, hotel.timezone),
              checkoutDate: moment.tz(booking.checkoutDate, hotel.timezone),
            });
          }

          if (
            sendKeys &&
            (booking.email || booking.phone) &&
            booking.checkinDate &&
            booking.checkoutDate
          )
            setStep(STEP_COMPLETE);
          else setStep(STEP_GUEST_INFO);
        },
        (error) => {
          dismissLoading();
          presentToastErrorContent(error);
          setStep(STEP_GUEST_INFO);
        },
      );
    } else {
      setStep(STEP_GUEST_PHONE);
    }
  }, [guestId, hotel.timezone, sendKeys]);

  function handlePhoneInputChange(phone) {
    setPhoneNumberError("");
    let phoneObj = phone && parsePhoneNumber(phone);
    let areacode = "+" + ((phoneObj && phoneObj.countryCallingCode) || "");
    setFormValues({ ...formValues, phone, areacode });
  }

  function handleInputChange({ target: { name, value } }) {
    var values = { ...formValues };
    values[name] = value;
    setFormValues(values);
  }

  function handleRoomInputChange({ name, roomType }) {
    var values = { ...formValues, roomNumber: name };
    if (roomType) values.roomType = roomType;
    setFormValues(values);
  }

  // function handleRoomTypeInputChange({target:{name, value}}) {
  //   var values = {...formValues, roomType:value};
  //   if (formValues.roomNumber && formValues.roomNumber !== "-" && formValues.roomType === 0)
  //   values.roomNumber = "-";
  //   setFormValues(values);
  // }

  function handleCheckboxChange({ target: { name, checked } }) {
    var values = { ...formValues };
    values[name] = checked;
    setFormValues(values);
  }

  function handleBookingDatesChange(checkinDate, checkoutDate) {
    var checkinDateString = moment(checkinDate).format("YYYY-MM-DD");
    var checkoutDateString = moment(checkoutDate).format("YYYY-MM-DD");
    if (hotel.checkinTime)
      checkinDate = moment.tz(
        checkinDateString + " " + hotel.checkinTime,
        hotel.timezone,
      );
    if (hotel.checkoutTime)
      checkoutDate = moment.tz(
        checkoutDateString + " " + hotel.checkoutTime,
        hotel.timezone,
      );
    var values = { ...formValues, checkinDate, checkoutDate };
    setFormValues(values);
  }

  const handleUpdate = () => {
    if (formValues._id !== 0) {
      presentLoading();
      //console.log(formValues);
      GuestService.updateBooking(formValues).then(
        (response) => {
          dismissLoading();
          if (response.data.ok) {
            if (step === STEP_CHECK_IN) {
              setStep(STEP_COMPLETE);
            } else {
              if (
                hasAvailableUpsells &&
                typeof formValues.offerEarlyCheckin !== "boolean"
              ) {
                setFormValues({
                  ...formValues,
                  offerEarlyCheckin:
                    hotel.offerEarlyCheckin &&
                    currentRoomType?.earlyCheckinPrice > 0,
                  offerUpgrade:
                    hotel.offerUpgrade && currentRoomType?.upgradePrice > 0,
                  offerLateCheckout:
                    hotel.offerLateCheckout &&
                    currentRoomType?.lateCheckoutPrice > 0,
                });
              }
              setStep(
                hotel.onlineCheckinEnabled && hasAvailableUpsells
                  ? STEP_CHECK_IN
                  : STEP_COMPLETE,
              );
            }
            if (response.data.modifiedCount || response.data.guestModified)
              presentToastSuccess(t("guestModal.detailsUpdated"));
          }
        },
        (error) => {
          dismissLoading();
          presentToastErrorContent(error);
        },
      );
    }
  };

  const onCancel = (refresh) => {
    if (!guestId) {
      GuestService.deleteBooking(formValues._id).then(
        (response) => {},
        (error) => {},
      );
      onClose(true);
    } else {
      onClose(refresh);
    }
  };

  const handleEnterPhone = () => {
    if (
      isValidPhoneNumber(formValues.phone) ||
      formValues.phone === "+15550000000"
    ) {
      presentLoading();
      GuestService.addGuest({ phone: formValues.phone }).then(
        (response) => {
          dismissLoading();
          const booking = response.data;
          if (booking._id) {
            setStep(STEP_GUEST_INFO);
            setFormValues({
              ...defaultValues,
              ...booking,
              checkinDate: moment.tz(booking.checkinDate, hotel.timezone),
              checkoutDate: moment.tz(booking.checkoutDate, hotel.timezone),
              title: "Add",
            });
            if (booking.existing)
              presentToastSuccess(t("guestModal.travelerHasAccount"));
            else presentToastSuccess(t("guestModal.guestAdded"));
          }
        },
        (error) => {
          dismissLoading();
          presentToastErrorContent(error);
        },
      );
    } else {
      setPhoneNumberError(t("guestModal.validPhoneError"));
    }
  };

  const gotoReservation = () => {
    window.location.href = "/guests/" + formValues._id;
  };

  return (
    <Layer position="center" modal onEsc={onClose} onClickOutside={onClose}>
      {step === STEP_GUEST_PHONE && (
        <Box pad="medium" gap="small" width="medium">
          <Heading level={3} margin="none">
            {guestId === 0 ? t("guestModal.addTitle") : t("guestModal.editTitle")}
          </Heading>
          <Box direction="row" gap="small">
            <FormField
              label={t("guestModal.phoneNumber")}
              className="phoneInputField"
              error={phoneNumberError}
            >
              <PhoneInput
                value={formValues.phone}
                onChange={handlePhoneInputChange}
              />
              <div className="international">{formValues.phone}</div>
            </FormField>
          </Box>
          <Box direction="row" justify="between" margin={{ top: "medium" }}>
            <Button label={t("common.cancel")} onClick={onClose} />
            <Button label={t("common.next")} onClick={handleEnterPhone} primary />
          </Box>
        </Box>
      )}
      {step === STEP_GUEST_INFO && (
        <Box pad="medium" gap="small" width="medium">
          <Heading level={3} margin="none">
            {guestId === 0 ? t("guestModal.addTitle") : t("guestModal.editTitle")}
          </Heading>
          <Box direction="row" gap="small">
            <FormField label={t("guestModal.firstName")} alignSelf="end" fill>
              <TextInput
                name="firstname"
                value={formValues.firstname}
                onChange={handleInputChange}
              />
            </FormField>
            <FormField label={t("guestModal.lastName")} alignSelf="end" fill>
              <TextInput
                name="lastname"
                value={formValues.lastname}
                onChange={handleInputChange}
              />
            </FormField>
          </Box>
          <BookingCalendarInput
            checkinDate={formValues.checkinDate}
            checkoutDate={formValues.checkoutDate}
            onChange={handleBookingDatesChange}
          />
          <FormField label={t("guestModal.email")}>
            <TextInput
              name="email"
              value={formValues.email}
              onChange={handleInputChange}
              placeholder="example@my.com"
            />
          </FormField>
          <FormField label={t("guestModal.assignRoom")} fill>
            <RoomSelect
              value={formValues.roomNumber}
              onChange={handleRoomInputChange}
            />
          </FormField>
          <Text alignSelf="center" color="dark-1" weight="bold">
            {t("guestModal.phoneNumberWithValue", {
              phone: formatPhoneNumberIntl(formValues.phone),
            })}
          </Text>
          <Box direction="row" justify="between" margin={{ top: "medium" }}>
            <Button label={t("common.cancel")} onClick={onCancel} />
            <Button label={t("common.save")} onClick={handleUpdate} primary />
          </Box>
        </Box>
      )}
      {step === STEP_CHECK_IN && (
        <Box pad="medium" gap="small" width="large">
          {(hotel.offerEarlyCheckin ||
            hotel.offerUpgrade ||
            hotel.offerLateCheckout) && (
            <Heading level={4} margin="none">
              {t("checkinFlow.upsell.title")}
            </Heading>
          )}
          {hotel.offerEarlyCheckin && (
            <CheckBox
              name="offerEarlyCheckin"
              checked={
                formValues.offerEarlyCheckin &&
                currentRoomType?.earlyCheckinPrice > 0
              }
              onChange={handleCheckboxChange}
              disabled={!(currentRoomType?.earlyCheckinPrice > 0)}
              label={
                <Box direction="row" gap="xsmall" align="center">
                  <Text>{t("settingsRoomTypes.earlyCheckin")}</Text>
                  {currentRoomType?.earlyCheckinPrice > 0 && (
                    <Text size="small" color="dark-6">
                      ({hotel.currency} {currentRoomType.earlyCheckinPrice})
                    </Text>
                  )}
                </Box>
              }
            />
          )}
          {hotel.offerUpgrade && (
            <CheckBox
              name="offerUpgrade"
              checked={
                formValues.offerUpgrade && currentRoomType?.upgradePrice > 0
              }
              onChange={handleCheckboxChange}
              disabled={!(currentRoomType?.upgradePrice > 0)}
              label={
                <Box direction="row" gap="xsmall" align="center">
                  <Text>{t("settingsUpsell.offerRoomUpgrade")}</Text>
                  {currentRoomType?.upgradePrice > 0 && (
                    <Text size="small" color="dark-6">
                      ({hotel.currency} {currentRoomType.upgradePrice})
                    </Text>
                  )}
                </Box>
              }
            />
          )}
          {hotel.offerLateCheckout && (
            <CheckBox
              name="offerLateCheckout"
              checked={
                formValues.offerLateCheckout &&
                currentRoomType?.lateCheckoutPrice > 0
              }
              onChange={handleCheckboxChange}
              disabled={!(currentRoomType?.lateCheckoutPrice > 0)}
              label={
                <Box direction="row" gap="xsmall" align="center">
                  <Text>{t("settingsUpsell.offerLateCheckout")}</Text>
                  {currentRoomType?.lateCheckoutPrice > 0 && (
                    <Text size="small" color="dark-6">
                      ({hotel.currency} {currentRoomType.lateCheckoutPrice})
                    </Text>
                  )}
                </Box>
              }
            />
          )}
          <Box direction="row" justify="between" margin={{ top: "medium" }}>
            <Button label={t("common.close")} onClick={() => onCancel(true)} />
            {formValues.invitationSent ? (
              <Button
                label={t("common.update")}
                onClick={handleUpdate}
                primary
                color="brand"
              />
            ) : (
              <Button
                label={t("guestModal.createReservation")}
                onClick={handleUpdate}
                primary
                color="brand"
              />
            )}
          </Box>
        </Box>
      )}
      {step === STEP_COMPLETE && (
        <Box pad="medium" gap="small" width="medium">
          <Heading level={4} margin="none">
            {t("guestModal.reservationCreated")}
          </Heading>
          <Text center>
            {t("guestModal.reservationCreatedMessage", {
              firstName: formValues.firstname,
              lastName: formValues.lastname,
              room: formValues.roomNumber,
              checkin: formValues.checkinDate.format("LLLL"),
              checkout: formValues.checkoutDate.format("LLLL"),
            })}
          </Text>
          <Box
            direction="row"
            justify="between"
            margin={{ top: "medium" }}
            className="buttons"
          >
            <Button label={t("common.close")} onClick={() => onClose(true)} />
            <Button
              label={t("guestModal.goToReservation")}
              onClick={() => gotoReservation()}
              primary
              color="brand"
            />
          </Box>
        </Box>
      )}
    </Layer>
  );
}
